import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isSsrShellForeign, removeSsrShell } from '../utils/ssrShell';

/**
 * Shell rule (R15), cross-route half — the single point that reacts to route
 * changes for every public route (T038-FE, B1).
 *
 * A shell *kept* on route A would sit on top of route B forever: on `/` and
 * `/esplora` no per-route effect ever removes it, because those routes have
 * no territory resolution by definition. Here the shell is judged only
 * against the document it was rendered for — a pathname mismatch means
 * foreign, and the idempotent `removeSsrShell` runs. Full rationale in
 * `ssrShell.ts`.
 *
 * Placement contract: call it in the route tree's component body, outside
 * any `<Routes>`, so `useLocation()` reads the router's real location —
 * never the synthetic one a child `<Routes location={…}>` may receive (a
 * desktop `/event/:id` draws over a synthetic `/esplora` background).
 * Releasing a territory shell when the event modal opens over it is
 * intended: the event document has its own decision in `EventModal`.
 *
 * The release is forced (T102-INT): a foreign pathname wins over a shell the
 * handoff scaffold kept because the user had scrolled or focused inside it,
 * and the root becomes usable. See `removeSsrShell`.
 *
 * First render is always a no-op — the pathname is still the shell's own
 * document. Query-only changes never count (`isSsrShellForeign` compares
 * the pathname alone), so the Esplora bootstrap stripping URL params on
 * mount cannot release the shell of the page the user is still on.
 */
export function useSsrShellRouteGuard(): void {
  const { pathname } = useLocation();

  useEffect(() => {
    // `force`: a foreign pathname wins over a shell kept because the user
    // interacted with it (T102-INT) — the root must become usable.
    if (isSsrShellForeign(pathname)) removeSsrShell({ force: true });
  }, [pathname]);
}
